import React, { useEffect, useState, useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDeleteDriverMutation, useGetDriverQuery } from "../../../Api/attoDeskApi";
import { IDriver } from "../../../Api/Interface/api.interface";
import NewPopUpDriver from "../Edit/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import Pagination from "../../../Components/Pagination";
import { CiSearch } from "react-icons/ci";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const cap = (s?: string | null) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

const initials = (first?: string, last?: string) =>
  `${(first || "?")[0]}${(last || "")[0] || ""}`.toUpperCase();

const ROLE_CFG: Record<string, { label: string; color: string; bg: string }> = {
  driver:      { label: "Driver",      color: "#2563eb", bg: "#dbeafe" },
  manager:     { label: "Manager",     color: "#2f3891", bg: "#ede9fe" },
  super_admin: { label: "Super Admin", color: "#0f766e", bg: "#ccfbf1" },
};
const getRole = (r?: string) =>
  ROLE_CFG[r || ""] ?? { label: r || "—", color: "#64748b", bg: "#f1f5f9" };

// ─── Component ────────────────────────────────────────────────────────────────
const Index = () => {
  const navigate = useNavigate();
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;
  const { showErrorMessage, showMessage } = useNotifier();

  const [searchQuery, setSearchQuery]     = useState("");
  const [currentSearch, setCurrentSearch] = useState("");
  const [filterRole, setFilterRole]       = useState("");
  const [page, setPage]                   = useState(0);
  const rowsPerPage                       = 50;

  const [editTarget, setEditTarget]   = useState<IDriver | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IDriver | null>(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data, isLoading, isError, refetch } = useGetDriverQuery({
    searchText: currentSearch, id: "",
  });

  useEffect(() => {
    if (debouncedSearch.length >= 2 || debouncedSearch === "") {
      setCurrentSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  const [deleteDriver] = useDeleteDriverMutation();

  const handleDelete = async (id: string) => {
    try {
      const res = await deleteDriver(id).unwrap();
      if (res.status) { showMessage("Deleted successfully"); setDeleteTarget(null); }
      else showErrorMessage("Failed to delete");
    } catch { showErrorMessage("Failed to delete"); }
  };

  const allDrivers: IDriver[] = Array.isArray(data?.data) ? (data!.data as IDriver[]) : [];

  const filtered = useMemo(() => {
    return allDrivers.filter((d) => !filterRole || d.driverUserRole === filterRole);
  }, [allDrivers, filterRole]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const thSx = {
    fontWeight: 700, fontSize: 11, color: "#64748b",
    textTransform: "uppercase" as const, letterSpacing: "0.06em",
    py: 1.5, px: 2, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc",
  };
  const tdSx = { py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9", fontSize: 13, color: "#1e293b" };

  if (isLoading)
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress size={32} /></Box>;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, background: "linear-gradient(135deg,#2f3891,#2563eb)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <GroupsOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", lineHeight: 1 }}>Drivers</Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>{filtered.length} driver{filtered.length !== 1 ? "s" : ""}</Typography>
        </Box>
        {/* Add action removed — drivers are created from User Management. */}
      </Box>

      {/* Filters */}
      <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", p: 2, mb: 2, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small" placeholder="Search by name or mobile…"
          value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><CiSearch style={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment>,
            sx: { borderRadius: 2, fontSize: 13, bgcolor: "#fff", height: 36, minWidth: 220 },
          }}
          sx={{ "& .MuiOutlinedInput-root": { "&:hover fieldset": { borderColor: "#2f3891" }, "&.Mui-focused fieldset": { borderColor: "#2f3891" } } }}
        />
        <Select
          size="small" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} displayEmpty
          sx={{ fontSize: 13, borderRadius: 2, height: 36, minWidth: 150, bgcolor: "#fff", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } }}
        >
          <MenuItem value="">All Roles</MenuItem>
          <MenuItem value="driver">Driver</MenuItem>
          <MenuItem value="manager">Manager</MenuItem>
          <MenuItem value="super_admin">Super Admin</MenuItem>
        </Select>
        {(searchQuery || filterRole) && (
          <Button size="small" onClick={() => { setSearchQuery(""); setFilterRole(""); setCurrentSearch(""); }}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", border: "1px solid #e2e8f0" }}>
            Clear
          </Button>
        )}
      </Box>

      {/* Table */}
      <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>#</TableCell>
                <TableCell sx={thSx}>Driver</TableCell>
                <TableCell sx={thSx}>Role</TableCell>
                <TableCell sx={thSx}>Mobile</TableCell>
                <TableCell sx={thSx}>Email</TableCell>
                <TableCell sx={thSx}>City</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "right" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isError || paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <GroupsOutlinedIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                      <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>
                        {isError ? "Error fetching data" : filtered.length === 0 && filterRole ? "No drivers match this role" : "No drivers yet"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((row, index) => {
                  const role = getRole(row.driverUserRole);
                  const imgSrc = row.driverImg ? `${LARAVEL_API_URL}/${row.driverImg}` : null;
                  const fullName = `${cap(row.driverFirstName)} ${cap(row.driverLastName)}`.trim();

                  return (
                    <TableRow key={row.id}
                      sx={{ "&:hover": { bgcolor: "#fafcff" }, "&:last-child td": { borderBottom: 0 }, cursor: "default" }}
                    >
                      <TableCell sx={{ ...tdSx, color: "#94a3b8", width: 48 }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      {/* Name + avatar */}
                      <TableCell sx={tdSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          {imgSrc ? (
                            <Box
                              component="img"
                              src={imgSrc}
                              alt={fullName}
                              sx={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "2px solid #e2e8f0" }}
                              onError={(e: any) => { e.target.style.display = "none"; }}
                            />
                          ) : (
                            <Avatar sx={{ width: 36, height: 36, fontSize: 13, fontWeight: 700, bgcolor: "#2f3891", flexShrink: 0 }}>
                              {initials(row.driverFirstName, row.driverLastName)}
                            </Avatar>
                          )}
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>{fullName || "—"}</Typography>
                            {row.driverUserName && (
                              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>@{row.driverUserName}</Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Role badge */}
                      <TableCell sx={tdSx}>
                        <Chip label={role.label} size="small"
                          sx={{ height: 22, fontSize: 11, fontWeight: 700, color: role.color, bgcolor: role.bg, border: `1px solid ${role.color}33` }} />
                      </TableCell>

                      {/* Mobile — clickable */}
                      <TableCell sx={tdSx}>
                        {row.driverMobileNum ? (
                          <Box component="a" href={`tel:${row.driverMobileNum}`}
                            sx={{ color: "#2563eb", textDecoration: "none", fontWeight: 500, fontSize: 13, "&:hover": { textDecoration: "underline" } }}>
                            {row.driverMobileNum}
                          </Box>
                        ) : <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>—</Typography>}
                      </TableCell>

                      {/* Email */}
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: 13, color: "#475569" }}>{row.driverEmail || "—"}</Typography>
                      </TableCell>

                      {/* City */}
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: 13, color: "#475569" }}>{row.driverCity || "—"}</Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                          <Tooltip title="View profile" arrow>
                            <IconButton size="small" onClick={() => navigate(`/team-history/${row.id}`)}
                              sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: "#f0f9ff", color: "#0ea5e9", "&:hover": { bgcolor: "#e0f2fe" } }}>
                              <VisibilitySharpIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit" arrow>
                            <IconButton size="small" onClick={() => setEditTarget(row)}
                              sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: "#eef2ff", color: "#2f3891", "&:hover": { bgcolor: "#e0e7ff" } }}>
                              <EditSharpIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton size="small" onClick={() => setDeleteTarget(row)}
                              sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: "#fff1f2", color: "#e11d48", "&:hover": { bgcolor: "#ffe4e6" } }}>
                              <DeleteOutlineSharpIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {filtered.length > rowsPerPage && (
          <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end" }}>
            <Pagination count={filtered.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(_, p) => setPage(p)} />
          </Box>
        )}
      </Box>

      {/* Edit dialog */}
      {editTarget && (
        <NewPopUpDriver
          openModel={true}
          handleCloseDialog={() => { setEditTarget(null); refetch(); }}
          data={editTarget}
        />
      )}

      {/* Delete dialog */}
      {deleteTarget && (
        <DeletePopup
          open={true}
          handleCloseDelete={() => setDeleteTarget(null)}
          onConfirm={async () => handleDelete(deleteTarget.id.toString())}
          title="Delete Driver"
          content={`Are you sure you want to delete "${cap(deleteTarget.driverFirstName)} ${cap(deleteTarget.driverLastName)}"?`}
        />
      )}
    </Box>
  );
};

export default Index;
